import { cn } from '@/utils/cn';
import { toPaise } from '@/utils/money';

import { AMOUNT_SLABS, slabAmount } from './amount-slabs';
import styles from './AmountSlabs.module.css';

export interface AmountSlabsProps {
  /** What the percentages are of — a sale total, or what is still due on a bill. */
  basePaise: number;
  /** Receives the money string ("1500.00") the tapped slab works out to. */
  onPick: (amount: string) => void;
  /** The amount in the field now, so the matching slab can show as chosen. */
  value?: string | undefined;
  className?: string | undefined;
}

/** Quick-pick buttons that fill an amount field. The field stays editable afterwards. */
export function AmountSlabs({ basePaise, onPick, value, className }: AmountSlabsProps) {
  // On a tiny balance the small shares round to ₹0, and the server rejects a zero payment —
  // so those buttons are left out rather than offered as a dead end.
  const offered = AMOUNT_SLABS.map((percent) => ({
    percent,
    amount: slabAmount(basePaise, percent),
  })).filter(({ amount }) => toPaise(amount) > 0);

  if (basePaise <= 0 || offered.length === 0) return null;

  return (
    <div className={cn(styles.slabs, className)} role="group" aria-label="Part payment">
      {offered.map(({ percent, amount }) => (
        <button
          key={percent}
          type="button"
          aria-pressed={value === amount}
          className={cn(styles.slab, value === amount && styles.selected)}
          onClick={() => {
            onPick(amount);
          }}
        >
          {percent}%
        </button>
      ))}
    </div>
  );
}
